import type { CareEventInput } from "@/api/careProtocol/CareProtocolApi";
import { queryKeys } from "@/api/queryKeys";
import type { QueryClient } from "@tanstack/react-query";

export const INTAKE_WEIGHT_RIGHTS_MESSAGE =
  "Das Tier wurde gespeichert. Das Aufnahmegewicht konnte wegen fehlender Rechte nicht gespeichert werden.";

export const INTAKE_WEIGHT_INVALID_MESSAGE =
  "Das Aufnahmegewicht muss eine Zahl größer als 0 sein.";

export const INTAKE_WEIGHT_THOUSANDS_MESSAGE =
  "Bitte ohne Tausenderpunkt eingeben.";

export const INTAKE_WEIGHT_MAX_GRAMS = 100000;

export const INTAKE_WEIGHT_MAX_MESSAGE =
  "Das Aufnahmegewicht darf höchstens 100000 g sein.";

export const INTAKE_WEIGHT_LOCKED_HINT =
  "Das Aufnahmegewicht dieses Versuchs ist festgelegt.";

export const INTAKE_WEIGHT_RETRY_FALLBACK =
  "Das Tier wurde gespeichert. Das Aufnahmegewicht konnte nicht gespeichert werden.";

/** Servertext aus submit_care_entry, wenn can_write_clinical_events fehlschlägt. */
export const CLINICAL_WRITE_DENIED =
  "not allowed to write clinical care events for this case";

export type ParsedIntakeWeight =
  | { ok: true; grams: number | null }
  | { ok: false; message: string };

/** Punkt als Tausendergruppe (1.234 oder 1.234,5), nicht als Dezimaltrenner. */
const THOUSANDS_SEPARATOR = /^\d{1,3}(\.\d{3})+(,\d+)?$/;

/**
 * Leeres Feld: kein Ereignis. Komma bleibt der Dezimaltrenner.
 * Ein Tausenderpunkt, eine nicht positive Zahl oder mehr als 100000 g
 * ist ein Fehler, bevor der Fall angelegt wird.
 */
export function parseIntakeWeight(raw: string): ParsedIntakeWeight {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, grams: null };
  if (THOUSANDS_SEPARATOR.test(trimmed)) {
    return { ok: false, message: INTAKE_WEIGHT_THOUSANDS_MESSAGE };
  }
  const grams = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(grams) || grams <= 0) {
    return { ok: false, message: INTAKE_WEIGHT_INVALID_MESSAGE };
  }
  if (grams > INTAKE_WEIGHT_MAX_GRAMS) {
    return { ok: false, message: INTAKE_WEIGHT_MAX_MESSAGE };
  }
  return { ok: true, grams };
}

/** Text fürs Feld, ohne dass der Screen die Eigenschaft message liest. */
export function intakeWeightErrorText(raw: string): string | null {
  const parsed = parseIntakeWeight(raw);
  if (parsed.ok === false) return parsed.message;
  return null;
}

export function isClinicalWriteDenied(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" && error !== null && "message" in error
        ? String((error as { message?: unknown }).message ?? "")
        : typeof error === "string"
          ? error
          : "";
  return message.includes(CLINICAL_WRITE_DENIED);
}

export type IntakeWeightSlot = {
  caseId: string;
  clientEntryId: string;
  recordedAt: string;
  grams: number;
  saved: boolean;
  abandoned: boolean;
};

export type IntakeWeightOutcome =
  | { kind: "ok" }
  | { kind: "rights" }
  | { kind: "retry"; error: unknown };

export function intakeWeightEvent(grams: number): CareEventInput {
  return { event_type: "weight", weight_grams: grams, sort_index: 1 };
}

/** Dieselben Keys wie useSubmitCareEntry, damit Chronik und Akte das Gewicht sehen. */
export function invalidateIntakeWeightQueries(qc: QueryClient, caseId: string) {
  void qc.invalidateQueries({ queryKey: ["care", "chronik", caseId] });
  void qc.invalidateQueries({ queryKey: ["care", "publicSummary", caseId] });
  void qc.invalidateQueries({ queryKey: ["therapy", "sidebar", caseId] });
  void qc.invalidateQueries({ queryKey: ["therapy", "dues", caseId] });
  void qc.invalidateQueries({ queryKey: ["therapy", "reviews"] });
  void qc.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) });
  void qc.invalidateQueries({ queryKey: queryKeys.cases.my });
}

/**
 * Speichert offene Gewichte. Fehlende Rechte markiert den Slot als aufgegeben
 * und wird nicht erneut versucht. Andere Fehler bleiben offen, dieselbe
 * clientEntryId gilt beim nächsten Versuch. Ein schon gespeicherter Fall
 * wird hier nicht erneut angelegt.
 */
export async function settleIntakeWeights(
  slots: IntakeWeightSlot[],
  submit: (slot: IntakeWeightSlot) => Promise<void>
): Promise<IntakeWeightOutcome> {
  let rights = false;
  let retryError: unknown = null;
  for (const slot of slots) {
    if (slot.saved || slot.abandoned || !(slot.grams > 0)) continue;
    try {
      await submit(slot);
      slot.saved = true;
    } catch (error) {
      if (isClinicalWriteDenied(error)) {
        slot.abandoned = true;
        rights = true;
      } else if (!retryError) {
        retryError = error;
      }
    }
  }
  if (retryError) return { kind: "retry", error: retryError };
  if (rights || slots.some((slot) => slot.abandoned)) return { kind: "rights" };
  return { kind: "ok" };
}
